import numbro from 'numbro'

import toSubscriptNumber from './toSubscriptNumber'

export const formatDollarAmount = (num: string | number | null | undefined, digits = 2, round = true) => {
  if (typeof num === 'string') num = Number(num)
  if (num === 0) return '$0'
  if (!num) return '-'
  if (num < 0.01 && digits <= 3) {
    return '<$0.01'
  }

  return numbro(num)
    .formatCurrency({
      average: round,
      mantissa: num > 1000 ? 2 : digits,
      totalLength: 3,
      thousandSeparated: true,
      abbreviations: {
        million: 'M',
        billion: 'B',
        trillion: 'T',
      },
    })
    .replace('m', 'M')
    .replace('b', 'B')
    .replace('t', 'T')
    .replace(/(\.\d+)?([kMBT])/g, (_match, decimals, suffix) => {
      if (!decimals) return suffix
      const trimmed = decimals.replace(/0+$/, '')
      return trimmed === '.' ? suffix : `${trimmed}${suffix}`
    })
}

function formatSmallNumber(num: number): string {
  // Option 1: Show meaningful decimal places (recommended)
  if (num >= 0.000001) {
    // For numbers >= 0.000001, show up to 6 decimal places
    return num.toFixed(6).replace(/\.?0+$/, '')
  } else if (num >= 0.0000001) {
    // For numbers >= 0.0000001, show up to 7 decimal places
    return num.toFixed(7).replace(/\.?0+$/, '')
  } else if (num >= 0.00000001) {
    // For numbers >= 0.00000001, show up to 8 decimal places
    return num.toFixed(8).replace(/\.?0+$/, '')
  } else {
    // For tiny numbers, show threshold
    return '<0.00000001'
  }
}

export const formatAmount = (num: number | string, digits = 2) => {
  if (typeof num === 'string') num = Number(num)
  if (num === 0) return '0'
  if (!num) return '-'
  if (typeof num === 'number' && num > 0 && num < 0.001) {
    return formatSmallNumber(num) || '<0.001'
  }

  // For numbers less than 1000, format without abbreviations
  if (num < 1000) {
    return numbro(num).format({
      thousandSeparated: true,
      mantissa: digits,
    })
  }

  if (num >= Number.MAX_SAFE_INTEGER) {
    return '>9Qa'
  }

  return numbro(num)
    .format({
      average: true,
      mantissa: num >= 1000 ? 2 : digits,
      thousandSeparated: true,
      totalLength: 3,
      abbreviations: {
        thousand: 'K',
        million: 'M',
        billion: 'B',
        trillion: 'T',
      },
    })
    .replace('m', 'M')
    .replace('b', 'B')
    .replace('t', 'T')
}

const SIGNIFICANT_DIGITS = 3
// From this many zeros after the point on, they collapse into a subscript count: 0.0₅27.
const SUBSCRIPT_ZEROS_FROM = 4

/** A number below 1 whose leading zeros are shown as a count: 0.0₅406 is { zeros: 5, digits: '406' }. */
export type CollapsedZeros = { zeros: number; digits: string }

/**
 * Formats a positive number below 1 by its leading significant digits, so tiny
 * prices and balances never round to 0.00. Returns a plain string (0.00248), or
 * the parts to render with a zero count when the zeros are many (0.0₅406).
 */
export function formatSignificantParts(num: number): string | CollapsedZeros {
  if (!(num > 0) || !Number.isFinite(num)) return '0'
  if (num >= 1) return formatAmount(num)
  // Exponent notation rounds to significant digits at any magnitude, carry included (0.0099999 -> 1.00e-2).
  const [mantissa, exponentText] = num.toExponential(SIGNIFICANT_DIGITS - 1).split('e')
  const exponent = Number(exponentText)
  // Only reached when rounding carries up to 1 (0.9996).
  if (exponent >= 0) return '1'
  const digits = mantissa.replace('.', '').replace(/0+$/, '')
  const zeros = -exponent - 1
  return zeros < SUBSCRIPT_ZEROS_FROM ? `0.${'0'.repeat(zeros)}${digits}` : { zeros, digits }
}

/** {@link formatSignificantParts} as text, the zero count in Unicode subscript digits. */
export function formatSignificant(num: number): string {
  const parts = formatSignificantParts(num)
  return typeof parts === 'string' ? parts : `0.0${toSubscriptNumber(parts.zeros)}${parts.digits}`
}
